import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Pause, Volume2, VolumeX, X } from 'lucide-react'
import { TourChecklist } from './TourChecklist'
import { TourTheirNumbers } from './TourTheirNumbers'
import { backdropRects, inflate, placeWidget, type Rect } from './position'
import { useAnchorRect, useViewportSize } from './useAnchorRect'
import { renderTokenText, type TokenResolveOptions } from './tourTokens'
import {
  DEFAULT_HIGHLIGHT,
  DEFAULT_POSITION,
  DEFAULT_WIDGET_CONFIG,
  type TourCta,
  type TourDoc,
  type TourStepRef,
} from './tourTypes'
import type { TourAnchorState, TourEngine } from './useTourEngine'
import './tour.css'

/**
 * Renders the active step: backdrop, spotlight ring, and one guide widget.
 *
 * Two accessibility decisions worth stating, because they look like omissions:
 *
 *  - Tooltips and hotspots are **not** `aria-modal` and have **no focus trap**.
 *    The whole premise is that the real app underneath stays operable — a
 *    `click_anchor` step literally requires the visitor to use it. Only true
 *    modals (text / media / lead form) trap focus.
 *  - There is exactly **one** `aria-live` region, owned here rather than one
 *    per widget, so a step change announces once instead of N times.
 */

export type TourLayerProps = {
  doc: TourDoc
  engine: TourEngine
  tokens: TokenResolveOptions
  /** Rendered in place of the card for a `lead_form` step. */
  renderLeadForm?: (step: TourStepRef, onCaptured: (leadId?: string | null) => void) => React.ReactNode
  /** From useTourNarration; absent (or manifest-less) tours render no control. */
  narration?: {
    state: 'idle' | 'playing' | 'paused' | 'blocked'
    enabled: boolean
    hasFor: (stepId: string | null) => boolean
    toggle: () => void
    mute: () => void
    setNarrationEnabled: (on: boolean) => void
  }
}

const PORTAL_ID = 'fg-tour-root'

/** The app has no portal root; create one lazily so z-index stays ours. */
export function usePortalRoot(): HTMLElement | null {
  const [root] = useState<HTMLElement | null>(() => {
    if (typeof document === 'undefined') return null
    const existing = document.getElementById(PORTAL_ID)
    if (existing instanceof HTMLElement) return existing
    const el = document.createElement('div')
    el.id = PORTAL_ID
    document.body.appendChild(el)
    return el
  })
  useEffect(() => () => {
    if (root && root.parentElement && !root.childElementCount) root.parentElement.removeChild(root)
  }, [root])
  return root
}

export function TourLayer({ doc, engine, tokens, renderLeadForm, narration }: TourLayerProps) {
  const root = usePortalRoot()
  const viewport = useViewportSize()
  const anchorRect = useAnchorRect(engine.anchorElement)
  const [cardHeight, setCardHeight] = useState(180)
  const headingRef = useRef<HTMLHeadingElement | null>(null)
  const cardObserver = useRef<ResizeObserver | null>(null)

  // Callback ref rather than a read during render: the card's height depends on
  // authored copy, and placement must re-run when it changes.
  const cardRef = useCallback((node: HTMLDivElement | null) => {
    cardObserver.current?.disconnect()
    cardObserver.current = null
    if (!node) return
    setCardHeight(node.offsetHeight || 180)
    const ro = new ResizeObserver(() => setCardHeight(node.offsetHeight || 180))
    ro.observe(node)
    cardObserver.current = ro
  }, [])

  useEffect(() => () => cardObserver.current?.disconnect(), [])
  const current = engine.current
  const [chapterMenuOpen, setChapterMenuOpen] = useState(false)

  // Move focus to the step heading so keyboard and screen-reader users land on
  // the new content, without stealing it from a field mid-typing.
  useEffect(() => { setChapterMenuOpen(false) }, [current?.step.id])

  useEffect(() => {
    if (!current) return
    const active = document.activeElement as HTMLElement | null
    if (active && /^(input|textarea|select)$/i.test(active.tagName)) return
    headingRef.current?.focus({ preventScroll: true })
  }, [current?.step.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const t = useMemo(
    () => (text: string | null | undefined) => renderTokenText(String(text ?? ''), tokens),
    [tokens],
  )

  if (!root || !engine.active || !current) return null

  const { step, chapter, index } = current
  const widget = step.widget
  if (widget.kind === 'none') return null

  const isModal =
    widget.kind === 'text_modal' ||
    widget.kind === 'media_modal' ||
    widget.kind === 'lead_form' ||
    // A resolved anchor is what makes an anchored widget anchorable; without
    // one the step degrades to a centered modal rather than pointing at nothing.
    engine.anchorState === 'fallback' ||
    !anchorRect

  const highlight = 'highlight' in widget ? (widget.highlight || DEFAULT_HIGHLIGHT) : DEFAULT_HIGHLIGHT
  const position = 'position' in widget ? (widget.position || DEFAULT_POSITION) : DEFAULT_POSITION
  const config = 'config' in widget ? (widget.config || DEFAULT_WIDGET_CONFIG) : DEFAULT_WIDGET_CONFIG

  const hole: Rect | null =
    anchorRect && !isModal && highlight.spotlight
      ? inflate(anchorRect, highlight.paddingPx, viewport)
      : null

  const cardSize = { width: isModal ? 460 : 340, height: cardHeight }
  const placement =
    anchorRect && !isModal
      ? placeWidget({
          anchor: anchorRect,
          widget: cardSize,
          viewport,
          preferred: position.side,
          align: position.align,
        })
      : null

  const cardStyle: React.CSSProperties = placement
    ? { left: placement.left, top: placement.top }
    : {
        left: Math.max(12, viewport.width / 2 - cardSize.width / 2),
        top: Math.max(12, viewport.height / 2 - cardSize.height / 2),
      }

  const ctas: TourCta[] = widget.kind === 'lead_form' ? [] : ('ctas' in widget ? widget.ctas : null) || []
  const primary = ctas.find((c) => c.variant === 'primary')
  const secondaries = ctas.filter((c) => c.variant === 'secondary')

  // The card yields to the product. Any real interaction with the app —
  // a click outside the card, a scroll — collapses the card to a pulsing
  // pill and drops the focus ring, so exploring is never fighting the tour.
  // Clicking the pill resumes exactly where the visitor left off. Casey's
  // direction from using it: "if the user clicks anywhere on the application
  // outside the modal or scrolls, the modal box slides out of the way."
  const [collapsed, setCollapsed] = useState(false)
  const resumeAudioRef = useRef(false)
  useEffect(() => { setCollapsed(false) }, [step.id])
  useEffect(() => {
    if (widget.kind === 'lead_form' || widget.kind === 'their_numbers') return
    const controller = new AbortController()
    const shouldCollapse = (target: EventTarget | null): boolean => {
      if (collapsed) return false
      if (!(target instanceof Node)) return false
      const el = target instanceof Element ? target : target.parentElement
      if (!el) return false
      // The card, the checklist, and the pill are tour chrome, not the app.
      if (el.closest('.fg-tour__card, .fg-tour__checklist, .fg-tour__pill, .fg-tour__start')) return false
      // Clicking the highlighted anchor is the invited action, not a dismissal.
      if (engine.anchorElement && engine.anchorElement.contains(el)) return false
      return true
    }
    const collapse = () => {
      resumeAudioRef.current = narration?.state === 'playing'
      if (narration?.state === 'playing') narration.toggle()
      setCollapsed(true)
    }
    document.addEventListener('pointerdown', (e) => {
      if (shouldCollapse(e.target)) collapse()
    }, { capture: true, signal: controller.signal })
    document.addEventListener('wheel', (e) => {
      if (shouldCollapse(e.target)) collapse()
    }, { capture: true, passive: true, signal: controller.signal })
    document.addEventListener('touchmove', (e) => {
      if (shouldCollapse(e.target)) collapse()
    }, { capture: true, passive: true, signal: controller.signal })
    return () => controller.abort()
  }, [collapsed, step.id, widget.kind, engine.anchorElement, narration])

  const expand = () => {
    setCollapsed(false)
    if (resumeAudioRef.current && narration?.state === 'paused') narration.toggle()
    resumeAudioRef.current = false
  }

  const runCta = (cta: TourCta, variant: 'primary' | 'secondary' | 'convert' = cta.variant) => {
    engine.emitCta(variant, cta.label)
    const a = cta.action
    if (a.kind === 'next') engine.next()
    else if (a.kind === 'prev') engine.prev()
    else if (a.kind === 'end') engine.end('close_button')
    else if (a.kind === 'goto_step') engine.gotoStep(a.stepId)
    else if (a.kind === 'signup') window.location.href = `/signup?plan=${encodeURIComponent(a.plan || 'pro')}&utm_source=live_tour`
    else if (a.kind === 'external_url') {
      if (a.newTab) window.open(a.url, '_blank', 'noopener,noreferrer')
      else window.location.href = a.url
    }
  }

  const accent = doc.theme?.accent && /^#[0-9a-fA-F]{3,8}$/.test(doc.theme.accent) ? doc.theme.accent : null
  const logo = typeof tokens.audience?.logo === 'string' && /^https:\/\//.test(tokens.audience.logo)
    ? tokens.audience.logo
    : null

  const body = (
    <div
      className="fg-tour"
      data-fg-tour-step={step.id}
      data-fg-tour-anchor-state={engine.anchorState satisfies TourAnchorState}
      style={accent ? ({ '--fg-blue': accent, '--fg-blue-600': accent } as React.CSSProperties) : undefined}
    >

      {hole && highlight.spotlight && !collapsed ? (
        <div
          className="fg-tour__ring"
          style={{ left: hole.x, top: hole.y, width: hole.width, height: hole.height, borderRadius: highlight.radiusPx }}
        />
      ) : null}

      {widget.kind === 'hotspot' && anchorRect && !collapsed ? (
        <div className="fg-tour__hotspot" style={{ left: anchorRect.x + anchorRect.width / 2, top: anchorRect.y + anchorRect.height / 2 }} />
      ) : null}

      {placement?.arrow && position.arrow ? (
        <div className="fg-tour__arrow" style={{ left: placement.arrow.x - 5, top: placement.arrow.y - 5 }} />
      ) : null}

      {collapsed ? (
        <button type="button" className="fg-tour__pill" onClick={expand} aria-label="Resume the tour">
          ▶ Resume tour · {index + 1}/{engine.stepCount}
        </button>
      ) : widget.kind === 'lead_form' && renderLeadForm ? (
        // pointer-events is an INHERITED property: the layer root sets `none`
        // so the spotlight hole stays clickable, which silently made every
        // descendant of an unwrapped lead form click-transparent — the gate
        // rendered perfectly and could not capture anything.
        <div className="fg-tour__leadform">{renderLeadForm(current, engine.completeLeadStep)}</div>
      ) : (
        <div
          ref={cardRef}
          className={`fg-tour__card${isModal ? ' fg-tour__card--modal' : ''}`}
          style={cardStyle}
          role="dialog"
          aria-modal={isModal ? true : undefined}
          aria-labelledby={`fg-tour-title-${step.id}`}
        >
          <div className="fg-tour__eyebrow">
            {logo ? <img className="fg-tour__logo" src={logo} alt="" /> : null}
            <button
              type="button"
              className="fg-tour__chapterbtn"
              aria-haspopup="menu"
              aria-expanded={chapterMenuOpen}
              onClick={() => setChapterMenuOpen((open) => !open)}
            >
              {t(chapter.title)} ▾
            </button>
            {config.showStepNumber ? <span>{index + 1} / {engine.stepCount}</span> : null}
            {narration?.hasFor(step.id) ? (
              <span className="fg-tour__voice">
                {narration.enabled ? (
                  <>
                    <button
                      type="button"
                      className="fg-tour__voicebtn"
                      aria-label={narration.state === 'playing' ? 'Pause narration' : 'Play this step again'}
                      title={narration.state === 'playing' ? 'Pause' : 'Play this step again'}
                      onClick={narration.toggle}
                    >
                      {narration.state === 'playing' ? <Pause size={13} /> : <Volume2 size={13} />}
                    </button>
                    {/* Always present while narration is on — turning it off is
                        never more than one click away, at any point in the tour. */}
                    <button
                      type="button"
                      className="fg-tour__voicebtn"
                      aria-label="Turn narration off"
                      title="Turn narration off"
                      onClick={() => narration.setNarrationEnabled(false)}
                    >
                      <VolumeX size={13} />
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="fg-tour__voiceprompt"
                    onClick={() => narration.setNarrationEnabled(true)}
                  >
                    <Volume2 size={13} aria-hidden /> Narration off
                  </button>
                )}
              </span>
            ) : null}
          </div>
          {chapterMenuOpen ? (
            <div className="fg-tour__chapters" role="menu">
              {doc.chapters.map((c, ci) => (
                <button
                  key={c.id}
                  type="button"
                  role="menuitem"
                  className={`fg-tour__chapteritem${c.id === chapter.id ? ' is-current' : ''}`}
                  onClick={() => {
                    setChapterMenuOpen(false)
                    if (c.steps[0]) engine.gotoStep(c.steps[0].id)
                  }}
                >
                  <span style={{ opacity: 0.55 }}>{ci + 1}</span> {t(c.title)}
                </button>
              ))}
            </div>
          ) : null}

          <h2 id={`fg-tour-title-${step.id}`} className="fg-tour__title" ref={headingRef} tabIndex={-1}>
            {t(step.title)}
          </h2>

          {widget.kind === 'their_numbers' ? (
            <>
              {'body' in widget && widget.body ? <p className="fg-tour__body">{t(widget.body)}</p> : null}
              <TourTheirNumbers
                facts={tokens.facts || {}}
                submitLabel={'submitLabel' in widget ? widget.submitLabel : null}
                onCompute={engine.emitTheirNumbers}
              />
            </>
          ) : widget.kind === 'media_modal' ? (
            <>
              {widget.body ? <p className="fg-tour__body">{t(widget.body)}</p> : null}
              {widget.media.kind === 'image' ? (
                <img className="fg-tour__media" src={widget.media.src} alt={t(widget.media.alt)} />
              ) : (
                <video className="fg-tour__media" src={widget.media.src} poster={widget.media.poster || undefined} controls />
              )}
            </>
          ) : (
            <p className="fg-tour__body">
              {t('body' in widget ? widget.body : '')}
            </p>
          )}

          <div className="fg-tour__actions">
            <div className="fg-tour__dots" aria-hidden="true">
              {Array.from({ length: engine.stepCount }).map((_, i) => (
                <span
                  key={i}
                  className={`fg-tour__dot${i < index ? ' fg-tour__dot--done' : ''}${i === index ? ' fg-tour__dot--current' : ''}`}
                />
              ))}
            </div>
            <span className="fg-tour__spacer" />
            {config.showPrev && index > 0 ? (
              <button type="button" className="fg-tour__btn fg-tour__btn--link" onClick={engine.prev}>Back</button>
            ) : null}
            {secondaries.map((cta, i) => (
              <button
                key={`${cta.label}-${i}`}
                type="button"
                className="fg-tour__btn fg-tour__btn--ghost"
                onClick={() => runCta(cta)}
              >
                {t(cta.label)}
              </button>
            ))}
            <button
              type="button"
              className="fg-tour__btn fg-tour__btn--primary"
              onClick={() => (primary ? runCta(primary) : engine.next())}
            >
              {primary ? t(primary.label) : index + 1 >= engine.stepCount ? 'Finish' : 'Next'}
            </button>
          </div>

          {config.dismissible ? (
            <button
              type="button"
              className="fg-tour__btn fg-tour__btn--link"
              style={{ position: 'absolute', top: 8, right: 8, padding: 4 }}
              aria-label="End tour"
              onClick={() => engine.end('close_button')}
            >
              <X size={14} />
            </button>
          ) : null}
        </div>
      )}

      {doc.convertCta ? (
        <button
          type="button"
          className="fg-tour__convert"
          onClick={() => runCta(doc.convertCta as TourCta, 'convert')}
        >
          {t(doc.convertCta.label)}
        </button>
      ) : null}

      <TourChecklist doc={doc} engine={engine} tokens={tokens} />

      <div className="fg-tour__sr" aria-live="polite" role="status">
        {`Step ${index + 1} of ${engine.stepCount}. ${t(step.title)}`}
      </div>
    </div>
  )

  return createPortal(body, root)
}
